import React, { useEffect, useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Activity, Clock, Plus, ChevronRight, FileText, Users, TrendingUp, Search, X } from 'lucide-react';
import { AppShell } from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { API_URL, API_KEY } from '../config';

function timeAgo(ts) {
  const d = new Date(ts);
  const diff = Date.now() - d;
  const mins = Math.floor(diff / 60000);
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return d.toLocaleDateString();
}

// ─── Inline spinner style ─────────────────────────────────────────────────────
const spinnerStyle = {
  display: 'inline-block',
  width: 14,
  height: 14,
  border: '2px solid rgba(255,255,255,0.35)',
  borderTopColor: '#fff',
  borderRadius: '50%',
  animation: 'spin 0.7s linear infinite',
  flexShrink: 0,
};

// inject keyframes once
if (typeof document !== 'undefined' && !document.getElementById('monitor-spin-kf')) {
  const s = document.createElement('style');
  s.id = 'monitor-spin-kf';
  s.textContent = '@keyframes spin { to { transform: rotate(360deg); } }';
  document.head.appendChild(s);
}

// ─── Monitoring Modal ─────────────────────────────────────────────────────────
function MonitoringModal({ open, onClose, report }) {
  if (!open || !report) return null;

  const trend = (report.trend || 'Stable').toLowerCase().replace(/\s+/g, '_');

  const trendConfig = {
    improving:       { label: 'Improving',       bg: '#EAF3DE', color: '#27500A', border: '#97C459', dot: '#639922' },
    stable:          { label: 'Stable',           bg: '#F1EFE8', color: '#444441', border: '#B4B2A9', dot: '#888780' },
    declining:       { label: 'Declining',        bg: '#FCEBEB', color: '#791F1F', border: '#F09595', dot: '#E24B4A' },
    needs_attention: { label: 'Needs Attention',  bg: '#FAEEDA', color: '#633806', border: '#EF9F27', dot: '#BA7517' },
  };
  const tc = trendConfig[trend] || trendConfig.stable;

  const statusConfig = {
    improved:    { bg: '#EAF3DE', color: '#27500A', dot: '#639922', label: 'Improved' },
    'no change': { bg: '#F1EFE8', color: '#444441', dot: '#888780', label: 'Stable'   },
    worsened:    { bg: '#FCEBEB', color: '#791F1F', dot: '#E24B4A', label: 'Deteriorated' },
  };

  const NORMAL_VALUES = new Set(['normal', 'negative', 'none', 'undetected', 'absent', 'not detected']);

  const worseningKeys = new Set(
    (report.worsening_parameters || []).map(p => p.parameter || p.analysis_type)
  );
  const improvingKeys = new Set(
    (report.improving_parameters || []).map(p => p.parameter || p.analysis_type)
  );

  const getStatus = (item, severity) => {
    // Use pre-computed status for flat-parsed rows
    if (item._status) return item._status;
    const key    = item.parameter || item.analysis_type;
    const change = item.change || 0;
    const pct    = item.percent_change || 0;
    const isZeroBaseline  = item.earlier_value === 0 || item.earlier_value === '0';
    const hasActualChange = isZeroBaseline ? Math.abs(change) > 0 : Math.abs(pct) >= 5;
    if (!hasActualChange) return 'no change';
    const laterNormalised = NORMAL_VALUES.has((item.later_raw || '').toLowerCase().trim());
    if (laterNormalised) return 'improved';
    if (worseningKeys.has(key)) return 'worsened';
    if (improvingKeys.has(key)) return 'improved';
    if (severity === 'critical' || severity === 'significant') return 'worsened';
    if (severity === 'stable') return 'no change';
    return change < 0 ? 'improved' : 'worsened';
  };

  const reclassifySeverity = (item) => {
    const isZeroBaseline = item.earlier_value === 0 || item.earlier_value === '0';
    if (!isZeroBaseline) return item.severity;
    const absChange = Math.abs(item.change || 0);
    if (absChange === 0)  return 'stable';
    if (absChange >= 50)  return 'critical';
    if (absChange >= 20)  return 'significant';
    return 'moderate';
  };

  // ── Parse backend flat strings into structured rows ──────────────────────
  // Backend returns e.g. area_change: "5.2 → 3.1 cm²", depth_change: "1.0 → 0.6 cm"
  const parseChangeString = (str) => {
    if (!str || str === 'N/A') return null;
    // Matches "5.2 → 3.1 cm²" or "5.2 → 3.1 cm"
    const m = str.match(/([\d.]+)\s*→\s*([\d.]+)\s*(.*)/);
    if (!m) return null;
    return { earlier: parseFloat(m[1]), later: parseFloat(m[2]), unit: m[3].trim() };
  };

  const buildRow = (label, changeStr, isWoundDimension = true) => {
    const parsed = parseChangeString(changeStr);
    if (!parsed) return null;
    const { earlier, later, unit } = parsed;
    const change = later - earlier;
    const pct    = earlier !== 0 ? parseFloat(((change / earlier) * 100).toFixed(1)) : null;
    const absPct = pct !== null ? Math.abs(pct) : Math.abs(change);
    const severity =
      absPct >= 30 ? 'critical'    :
      absPct >= 15 ? 'significant' :
      absPct >= 5  ? 'moderate'    : 'stable';
    // For wound dimensions: decrease = healing = improving
    const status = Math.abs(change) < 0.01 ? 'no change'
      : isWoundDimension
        ? (change < 0 ? 'improved' : 'worsened')
        : (change < 0 ? 'improved' : 'worsened');
    return {
      analysis_type:  label,
      parameter:      label,
      earlier_raw:    `${earlier} ${unit}`.trim(),
      later_raw:      `${later} ${unit}`.trim(),
      earlier_value:  earlier,
      later_value:    later,
      change:         parseFloat(change.toFixed(3)),
      percent_change: pct,
      unit,
      severity,
      _status: status,   // pre-computed so getStatus fallback picks it up
    };
  };

  // ── Helper: extract a named metric from a record's analysis/report arrays ──
  const extractFromRecord = (record, metricName) => {
    if (!record) return null;
    const candidates = [
      record.analysis, record.Analysis,
      record.report,   record.Report,
    ];
    for (const src of candidates) {
      let arr = src;
      if (typeof arr === 'string') { try { arr = JSON.parse(arr); } catch { continue; } }
      if (!Array.isArray(arr)) continue;
      for (const item of arr) {
        if (!item || typeof item !== 'object') continue;
        const key = item['Analysis Type'] || item.analysis_type || item.parameter || item.Parameter || '';
        if (key.toLowerCase().includes(metricName.toLowerCase())) {
          const val = item.Result ?? item.result ?? item.Value ?? item.value ?? '';
          const str = String(val).trim();
          if (str && str !== 'null' && str !== 'undefined' && str !== 'N/A') return str;
        }
      }
    }
    return null;
  };

  const sortedRecordsForExtract = (report.records || []).slice().sort(
    (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
  );
  const firstRecord = sortedRecordsForExtract[0] || null;
  const lastRecord  = sortedRecordsForExtract[sortedRecordsForExtract.length - 1] || null;

  const flatRows = [
    buildRow('Wound Area',  report.area_change,  true),
    buildRow('Wound Depth', report.depth_change, true),
    // Bacterial Status: now comes directly from backend as bacterial_status_first / bacterial_status_latest
    (() => {
      const earlier = report.bacterial_status_first  || extractFromRecord(firstRecord, 'Bacterial') || null;
      const later   = report.bacterial_status_latest || extractFromRecord(lastRecord,  'Bacterial') || null;
      const earlyNorm = (earlier || '').toLowerCase().trim();
      const laterNorm = (later  || '').toLowerCase().trim();
      const NORMAL = new Set(['normal', 'negative', 'none', 'undetected', 'absent', 'not detected', 'non-bacterial', 'no bacteria', 'no growth']);
      let _status = 'no change';
      if (earlier && later && earlyNorm !== laterNorm) {
        // bacterial → non-bacterial = improved (cleared infection)
        // non-bacterial → bacterial = worsened (new infection)
        const earlierClean = NORMAL.has(earlyNorm);
        const laterClean   = NORMAL.has(laterNorm);
        if (!earlierClean && laterClean)  _status = 'improved';
        else if (earlierClean && !laterClean) _status = 'worsened';
      }
      return {
        analysis_type:  'Bacterial Status',
        parameter:      'Bacterial Status',
        earlier_raw:    earlier || '—',
        later_raw:      later   || '—',
        earlier_value:  null,
        later_value:    null,
        change:         null,
        percent_change: null,
        unit:           null,
        severity:       _status === 'worsened' ? 'significant' : 'stable',
        _status,
      };
    })(),
  ].filter(Boolean);

  // Merge with any structured arrays the backend may also send
  const structuredRows = [
    ...(report.critical_changes    || []).map(r => ({ ...r, severity: 'critical'    })),
    ...(report.significant_changes || []).map(r => ({ ...r, severity: 'significant' })),
    ...(report.moderate_changes    || []).map(r => ({ ...r, severity: 'moderate'    })),
    ...(report.stable_parameters   || []).map(r => ({ ...r, severity: 'stable'      })),
  ];

  // Deduplicate: if backend already sent a structured row for the same label, prefer it
  const structuredLabels = new Set(structuredRows.map(r => (r.analysis_type || r.parameter || '').toLowerCase()));
  const mergedFlat = flatRows.filter(r => !structuredLabels.has(r.analysis_type.toLowerCase()));

  const allRows = [...mergedFlat, ...structuredRows]
    .map(r => ({ ...r, severity: reclassifySeverity(r) }))
    .sort((a, b) => {
      const order = { critical: 0, significant: 1, moderate: 2, stable: 3 };
      return (order[a.severity] ?? 3) - (order[b.severity] ?? 3);
    });

  const improved = allRows.filter(r => getStatus(r, r.severity) === 'improved').length;
  const worsened = allRows.filter(r => getStatus(r, r.severity) === 'worsened').length;
  const stable   = allRows.filter(r => getStatus(r, r.severity) === 'no change').length;

  // ── Build multi-visit comparison table ────────────────────────────────────
  const visitRecords = sortedRecordsForExtract;

  // Build a map: paramName -> array of { raw, numeric } indexed by visit position
  // We derive each visit's value by walking allRows; earliest = earlier_raw, latest = later_raw
  // For multi-visit support we use report.visit_values if available,
  // otherwise fall back to showing earlier + later columns.
  const hasVisitValues = allRows.some(r => Array.isArray(r.visit_values));

  // Helper: delta arrow + colour between two numeric strings
  const delta = (prev, curr) => {
    const p = parseFloat(prev);
    const c = parseFloat(curr);
    if (isNaN(p) || isNaN(c)) return null;
    const diff = c - p;
    if (Math.abs(diff) < 0.001) return { arrow: '→', color: '#888780', diff: 0 };
    return diff < 0
      ? { arrow: '↓', color: '#639922', diff }   // decrease = healing (wounds)
      : { arrow: '↑', color: '#E24B4A', diff };
  };

  return (
    <div
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, zIndex: 1000,
        background: 'rgba(0,0,0,0.45)',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        padding: '16px',
      }}
    >
      <div
        onClick={e => e.stopPropagation()}
        style={{
          background: '#fff', borderRadius: 16, width: '100%', maxWidth: 900,
          maxHeight: '92vh', overflowY: 'auto',
          boxShadow: '0 20px 60px rgba(0,0,0,0.2)',
          display: 'flex', flexDirection: 'column',
        }}
      >
        {/* ── Modal header ─────────────────────────────────────────────── */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '16px 20px', borderBottom: '1px solid var(--border)',
          position: 'sticky', top: 0, background: '#fff', zIndex: 1,
          borderRadius: '16px 16px 0 0',
        }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 700, color: 'var(--navy, #0f172a)', fontFamily: 'var(--font-display)' }}>
            Wound Monitoring Report
          </h3>
          <button
            onClick={onClose}
            style={{
              width: 32, height: 32, borderRadius: '50%', border: 'none',
              background: 'var(--border, #e2e8f0)', cursor: 'pointer',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <X size={15} color="#64748b" />
          </button>
        </div>

        {/* ── Modal body ────────────────────────────────────────────────── */}
        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 14 }}>

          {/* Meta strip */}
          <div style={{
            display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 10,
            background: '#fafafa', borderRadius: 12, padding: '14px 16px',
            border: '1px solid var(--border, #e2e8f0)',
          }}>
            {[
              ['Patient',            report.patient_name       || '—'],
              ['Patient ID',         report.patient_id         || '—'],
              ['Visits',             report.total_records      || '—'],
              ['Period',             report.monitoring_period  || '—'],
              ['Parameters tracked', report.parameters_tracked || '—'],
            ].map(([label, value]) => (
              <div key={label}>
                <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
                  {label}
                </p>
                <p style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy, #0f172a)', marginTop: 3, marginBottom: 0 }}>
                  {value}
                </p>
              </div>
            ))}
          </div>

          {/* Trend pill + status chips */}
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: 0 }}>
              Overall trend
            </p>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 6,
              padding: '4px 13px', borderRadius: 20, fontSize: 12, fontWeight: 700,
              background: tc.bg, color: tc.color, border: `1px solid ${tc.border}`,
            }}>
              <span style={{ width: 7, height: 7, borderRadius: '50%', background: tc.dot, display: 'inline-block', flexShrink: 0 }} />
              {tc.label}
            </span>
            {[
              { label: 'Improved', count: improved, bg: '#EAF3DE', color: '#27500A' },
              { label: 'Deteriorated', count: worsened, bg: '#FCEBEB', color: '#791F1F' },
              { label: 'Stable',   count: stable,   bg: '#F1EFE8', color: '#5F5E5A' },
            ].filter(s => s.count > 0).map(s => (
              <span key={s.label} style={{
                display: 'inline-flex', alignItems: 'center', gap: 4,
                padding: '3px 10px', borderRadius: 20, fontSize: 11, fontWeight: 700,
                background: s.bg, color: s.color,
              }}>
                {s.count} {s.label}
              </span>
            ))}
          </div>

          {/* ════════════════════════════════════════════════════════════════
              VISIT COMPARISON TABLE
              Shows each parameter × visit side-by-side with delta arrows
          ════════════════════════════════════════════════════════════════ */}
          {allRows.length > 0 && (
            <>
              <p style={{ fontSize: 11, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '4px 0 0' }}>
                Visit-by-Visit Comparison
              </p>

              <div style={{ border: '1px solid var(--border, #e2e8f0)', borderRadius: 12, overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto' }}>
                  {hasVisitValues ? (
                    /* ── Multi-visit: each visit is a column ── */
                    (() => {
                      const maxVisits = Math.max(...allRows.map(r => (r.visit_values || []).length));
                      const visitLabels = visitRecords.slice(0, maxVisits).map((v, i) => ({
                        label: `Visit ${i + 1}`,
                        date: v.timestamp ? new Date(v.timestamp).toLocaleDateString() : `#${i + 1}`,
                      }));

                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 500 }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Parameter</th>
                              {visitLabels.map((v, i) => (
                                <th key={i} style={{ ...thStyle, textAlign: 'center' }}>
                                  <div>{v.label}</div>
                                  <div style={{ fontSize: 10, fontWeight: 400, color: '#b0bac5', marginTop: 2 }}>{v.date}</div>
                                </th>
                              ))}
                              <th style={{ ...thStyle, textAlign: 'center' }}>Net Change</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allRows.map((item, i) => {
                              const isLast = i === allRows.length - 1;
                              const visits = item.visit_values || [item.earlier_raw, item.later_raw];
                              const statusKey = getStatus(item, item.severity);
                              const sc = statusConfig[statusKey] || statusConfig['no change'];
                              const firstVal = visits[0];
                              const lastVal  = visits[visits.length - 1];
                              const netDelta = delta(firstVal, lastVal);

                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafcff' }}>
                                  {/* Parameter */}
                                  <td style={{ ...tdStyle(isLast), fontWeight: 700, fontSize: 12, minWidth: 140 }}>
                                    {item.analysis_type || item.parameter || '—'}
                                    {item.unit && <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4, fontSize: 11 }}>({item.unit})</span>}
                                    {item.ref_range && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontWeight: 400 }}>Ref: {item.ref_range}</div>}
                                  </td>
                                  {/* Per-visit values with delta arrows between them */}
                                  {visits.map((val, vi) => {
                                    const prevVal = vi > 0 ? visits[vi - 1] : null;
                                    const d = prevVal !== null ? delta(prevVal, val) : null;
                                    return (
                                      <td key={vi} style={{ ...tdStyle(isLast), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12 }}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4 }}>
                                          {d && (
                                            <span style={{ fontSize: 13, fontWeight: 700, color: d.color, flexShrink: 0 }}>
                                              {d.arrow}
                                            </span>
                                          )}
                                          <span>{val ?? '—'}</span>
                                        </div>
                                      </td>
                                    );
                                  })}
                                  {/* Net change */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center' }}>
                                    {netDelta ? (
                                      <span style={{
                                        display: 'inline-flex', alignItems: 'center', gap: 4,
                                        padding: '3px 9px', borderRadius: 20,
                                        fontSize: 11, fontWeight: 700,
                                        background: sc.bg, color: sc.color,
                                      }}>
                                        <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, display: 'inline-block', flexShrink: 0 }} />
                                        {netDelta.arrow} {Math.abs(netDelta.diff).toFixed(1)}
                                      </span>
                                    ) : (
                                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                                    )}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()
                  ) : (
                    /* ── Two-visit fallback: Earlier vs Later ── */
                    (() => {
                      const sortedVisits = (report.records || []).slice().sort(
                        (a, b) => new Date(a.timestamp) - new Date(b.timestamp)
                      );
                      const v1Date = sortedVisits[0]?.timestamp
                        ? new Date(sortedVisits[0].timestamp).toLocaleDateString() : 'First Visit';
                      const v2Date = sortedVisits[sortedVisits.length - 1]?.timestamp
                        ? new Date(sortedVisits[sortedVisits.length - 1].timestamp).toLocaleDateString() : 'Last Visit';

                      return (
                        <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: 460 }}>
                          <thead>
                            <tr>
                              <th style={thStyle}>Parameter</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>
                                <div>First Visit</div>
                                <div style={{ fontSize: 10, fontWeight: 400, color: '#b0bac5', marginTop: 2 }}>{v1Date}</div>
                              </th>
                              <th style={{ ...thStyle, textAlign: 'center', width: 40 }}></th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>
                                <div>Last Visit</div>
                                <div style={{ fontSize: 10, fontWeight: 400, color: '#b0bac5', marginTop: 2 }}>{v2Date}</div>
                              </th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Change</th>
                              <th style={{ ...thStyle, textAlign: 'center' }}>Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {allRows.map((item, i) => {
                              const isLast    = i === allRows.length - 1;
                              const statusKey = getStatus(item, item.severity);
                              const sc        = statusConfig[statusKey] || statusConfig['no change'];
                              const pct       = item.percent_change || 0;
                              const isZeroBase = item.earlier_value === 0 || item.earlier_value === '0';

                              const arrowColor =
                                statusKey === 'improved'  ? '#639922' :
                                statusKey === 'worsened'  ? '#E24B4A' : '#888780';

                              const changeDisplay = (() => {
                                if (item.change === null) {
                                  // Text-based metric (e.g. Bacterial Status) — show directional arrow only
                                  if (statusKey === 'improved')  return <span style={{ color: '#639922', fontWeight: 700 }}>↓</span>;
                                  if (statusKey === 'worsened')  return <span style={{ color: '#E24B4A', fontWeight: 700 }}>↑</span>;
                                  return <span style={{ color: '#888780' }}>→</span>;
                                }
                                if (isZeroBase && Math.abs(item.change || 0) > 0) {
                                  const arrow = (item.change || 0) > 0 ? '↑' : '↓';
                                  return <>{arrow} {Math.abs(item.change).toFixed(1)}<span style={{ fontSize: 10, fontWeight: 400 }}> (new)</span></>;
                                }
                                if (pct === null || pct === 0) return <span style={{ color: '#888780' }}>→ 0%</span>;
                                // ↑ = increase = red (worse for wounds), ↓ = decrease = green (healing)
                                const arrow = pct > 0 ? '↑' : '↓';
                                const color = statusKey === 'improved' ? '#639922' : statusKey === 'worsened' ? '#E24B4A' : '#888780';
                                return <span style={{ color }}>{arrow} {Math.abs(pct).toFixed(1)}%</span>;
                              })();

                              const d = delta(item.earlier_value, item.later_value);

                              return (
                                <tr key={i} style={{ background: i % 2 === 0 ? '#fff' : '#fafcff' }}>
                                  {/* Parameter */}
                                  <td style={{ ...tdStyle(isLast), fontWeight: 700, fontSize: 12, minWidth: 140 }}>
                                    {item.analysis_type || item.parameter || '—'}
                                    {item.unit && <span style={{ fontWeight: 400, color: '#94a3b8', marginLeft: 4, fontSize: 11 }}>({item.unit})</span>}
                                    {item.ref_range && <div style={{ fontSize: 10, color: '#94a3b8', marginTop: 2, fontWeight: 400 }}>Ref: {item.ref_range}</div>}
                                  </td>
                                  {/* Earlier */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, color: '#64748b' }}>
                                    {item.earlier_raw || '—'}
                                  </td>
                                  {/* Arrow between columns */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center', padding: '10px 4px' }}>
                                    {d ? (
                                      <span style={{ fontSize: 16, fontWeight: 700, color: d.color }}>{d.arrow}</span>
                                    ) : (
                                      <span style={{ color: '#cbd5e1' }}>→</span>
                                    )}
                                  </td>
                                  {/* Later */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center', fontFamily: 'var(--font-mono)', fontSize: 12, fontWeight: 600 }}>
                                    {item.later_raw || '—'}
                                  </td>
                                  {/* % Change */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center' }}>
                                    <span style={{ color: arrowColor, fontWeight: 700, fontSize: 13 }}>
                                      {changeDisplay}
                                    </span>
                                  </td>
                                  {/* Status */}
                                  <td style={{ ...tdStyle(isLast), textAlign: 'center' }}>
                                    <span style={{
                                      display: 'inline-flex', alignItems: 'center', gap: 5,
                                      padding: '3px 9px', borderRadius: 20,
                                      fontSize: 11, fontWeight: 700,
                                      background: sc.bg, color: sc.color, whiteSpace: 'nowrap',
                                    }}>
                                      <span style={{ width: 6, height: 6, borderRadius: '50%', background: sc.dot, display: 'inline-block', flexShrink: 0 }} />
                                      {sc.label}
                                    </span>
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      );
                    })()
                  )}
                </div>
              </div>
            </>
          )}

          {allRows.length === 0 && (
            <div style={{
              textAlign: 'center', padding: '32px 0', background: '#fafafa',
              borderRadius: 12, border: '1px solid var(--border, #e2e8f0)',
              color: '#94a3b8', fontSize: 13,
            }}>
              No parameter changes detected between visits.
            </div>
          )}

          {/* Legend */}
          <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'center' }}>
            {[
              { label: 'Improved', dot: '#639922' },
              { label: 'Deteriorated', dot: '#E24B4A' },
              { label: 'Stable',   dot: '#888780' },
            ].map(({ label, dot }) => (
              <span key={label} style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 11, color: '#64748b' }}>
                <span style={{ width: 7, height: 7, borderRadius: '50%', background: dot, display: 'inline-block' }} />
                {label}
              </span>
            ))}
            <span style={{ fontSize: 11, color: '#94a3b8', marginLeft: 'auto' }}>
              For wounds: ↓ depth/area = healing
            </span>
          </div>

          {/* AI Recommendations */}
          {report.ai_advice && (
            <div style={{ background: '#fafafa', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--border, #e2e8f0)' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 7px' }}>
                AI Recommendations
              </p>
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.75, margin: 0, whiteSpace: 'pre-wrap' }}>
                {report.ai_advice}
              </p>
            </div>
          )}

          {/* Summary */}
          {(report.comparison_summary || report.comparison) && (
            <div style={{ background: '#fafafa', borderRadius: 12, padding: '14px 16px', border: '1px solid var(--border, #e2e8f0)' }}>
              <p style={{ fontSize: 10, fontWeight: 700, color: '#94a3b8', textTransform: 'uppercase', letterSpacing: '0.5px', margin: '0 0 7px' }}>
                Summary
              </p>
              <p style={{ fontSize: 13, color: '#334155', lineHeight: 1.75, margin: 0 }}>
                {report.comparison_summary || report.comparison}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Shared table cell styles ─────────────────────────────────────────────────
const thStyle = {
  padding: '9px 12px', fontSize: 11, fontWeight: 700,
  color: '#94a3b8', borderBottom: '1px solid var(--border, #e2e8f0)',
  textTransform: 'uppercase', letterSpacing: '0.5px',
  textAlign: 'left', background: '#fafafa', whiteSpace: 'nowrap',
};

const tdStyle = (isLast) => ({
  padding: '10px 12px', fontSize: 13,
  color: 'var(--navy, #0f172a)',
  borderBottom: isLast ? 'none' : '1px solid #f1f5f9',
  verticalAlign: 'middle',
});

// ─── Main Dashboard ───────────────────────────────────────────────────────────
export default function DashboardScreen() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  // Monitoring state
  const [monitorOpen, setMonitorOpen] = useState(false);
  const [monitorReport, setMonitorReport] = useState(null);
  const [monitorLoading, setMonitorLoading] = useState(false);

  useEffect(() => {
    const fetchHistory = async () => {
      const email = user?.email || localStorage.getItem('userEmail');
      if (!email) { setLoading(false); return; }
      try {
        const res = await fetch(
          `${API_URL}/api/results?email=${encodeURIComponent(email)}`,
          { headers: { 'Cytometrix-API-KEY': API_KEY } }
        );
        if (res.ok) {
          const data = await res.json();
          setResults(Array.isArray(data) ? data : (data.results || []));
        }
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    };
    fetchHistory();
  }, [user]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase();
    return results.filter(r =>
      !q ||
      (r.full_name || '').toLowerCase().includes(q) ||
      (r.patient_id || '').toLowerCase().includes(q)
    );
  }, [results, search]);

  const canMonitor = useMemo(() => {
    if (!search.trim() || filtered.length < 2) return false;
    const ids = new Set(filtered.map(r => r.patient_id));
    return ids.size === 1;
  }, [search, filtered]);

  const handleMonitor = async () => {
    setMonitorLoading(true);
    try {
      const sorted = [...filtered].sort((a, b) => new Date(a.timestamp) - new Date(b.timestamp));
      const first  = sorted[0];

      const payload = {
        patient_id:   first.patient_id,
        patient_name: first.full_name,
        records: sorted.map(r => ({
          timestamp:  r.timestamp,
          analysis:   r.Analysis,
          analysisId: r.AnalysisID || r.analysisId,
          report:     r.report,
          id:         r.id,
        })),
      };

      const res = await fetch(`${API_URL}/api/monitoring`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Cytometrix-API-KEY': API_KEY },
        body: JSON.stringify(payload),
      });
      const data = await res.json();
      if (res.ok) {
        // Attach the original records so the modal can extract per-visit metrics (e.g. Bacterial Status)
        setMonitorReport({ ...data, records: sorted });
        setMonitorOpen(true);
      }
    } catch (e) {
      console.error('Monitor error:', e);
    } finally {
      setMonitorLoading(false);
    }
  };

  const uniquePatients = useMemo(() => new Set(results.map(r => r.patient_id)).size, [results]);

  const hourName = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good morning';
    if (h < 17) return 'Good afternoon';
    return 'Good evening';
  };

  return (
    <AppShell title="Clinical Dashboard">
      <div className="page-header">
        <h1 className="page-title">{hourName()}, {user?.username || 'Clinician'}</h1>
        <p className="page-subtitle">Here's your wound analysis overview</p>
      </div>

      <div className="stats-grid">
        {[
          { label: 'Total Analyses',   value: results.length,          icon: Activity,   color: '#1B6CA8', bg: 'rgba(27,108,168,0.08)' },
          { label: 'Patients Tracked', value: uniquePatients,          icon: Users,      color: '#059669', bg: 'rgba(5,150,105,0.08)' },
          { label: 'This Month',       value: results.filter(r => new Date(r.timestamp) > new Date(Date.now() - 30*86400000)).length, icon: TrendingUp, color: '#D97706', bg: 'rgba(217,119,6,0.08)' },
          { label: 'Latest Analysis',  value: results.length ? timeAgo(results[0]?.timestamp) : '—', icon: Clock, color: '#7C3AED', bg: 'rgba(124,58,237,0.08)', small: true },
        ].map(({ label, value, icon: Icon, color, bg, small }) => (
          <div className="stat-card" key={label}>
            <div className="stat-icon" style={{ background: bg, color }}>
              <Icon size={18} />
            </div>
            <div className="stat-label">{label}</div>
            <div className="stat-value" style={small ? { fontSize: '1.2rem', marginTop: 4 } : {}}>{value}</div>
          </div>
        ))}
      </div>

      {/* ── Analysis History header row ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 12 }}>
        <h2 style={{ fontFamily: 'var(--font-display)', fontSize: '1.25rem', fontWeight: 600, color: 'var(--navy)' }}>
          Analysis History
        </h2>
        <button className="btn btn-primary btn-sm" onClick={() => navigate('/patient')}>
          <Plus size={15} /> New Analysis
        </button>
      </div>

      {/* ── Search + Monitor row ── */}
      <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 20 }}>
        <div className="input-wrap" style={{ flex: 1, position: 'relative' }}>
          <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-muted)', pointerEvents: 'none' }} />
          <input
            className="form-control"
            style={{ paddingLeft: 36, paddingTop: 9, paddingBottom: 9, width: '100%' }}
            placeholder="Search by patient name or ID…"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
          {search && (
            <button
              onClick={() => setSearch('')}
              style={{ position: 'absolute', right: 10, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: 'var(--text-muted)', display: 'flex', alignItems: 'center' }}
            >
              <X size={14} />
            </button>
          )}
        </div>

        {canMonitor && (
          <button
            className="btn btn-sm"
            onClick={handleMonitor}
            disabled={monitorLoading}
            style={{
              display: 'flex', alignItems: 'center', gap: 6,
              background: '#059669', color: '#fff', border: 'none',
              borderRadius: 8, padding: '9px 14px', fontSize: '0.85rem',
              fontWeight: 600,
              cursor: monitorLoading ? 'not-allowed' : 'pointer',
              opacity: monitorLoading ? 0.85 : 1,
              whiteSpace: 'nowrap', flexShrink: 0,
              transition: 'opacity 0.2s',
            }}
          >
            {monitorLoading ? (
              /* ── Animated spinner ── */
              <>
                <span style={spinnerStyle} />
                Analysing…
              </>
            ) : (
              <>
                <TrendingUp size={15} />
                Monitor
              </>
            )}
          </button>
        )}
      </div>

      <div className="card" style={{ padding: 0 }}>
        {loading ? (
          <div className="loading-wrap">
            <div className="spinner spinner-lg" />
            <span>Loading history…</span>
          </div>
        ) : filtered.length === 0 ? (
          <div className="loading-wrap">
            <FileText size={40} style={{ color: 'var(--border)' }} />
            <p style={{ color: 'var(--text-muted)', fontSize: '0.9rem' }}>
              {search ? 'No results match your search.' : 'No analyses recorded yet. Start with a new analysis.'}
            </p>
            {!search && (
              <button className="btn btn-primary btn-sm" onClick={() => navigate('/patient')}>
                <Plus size={15} /> New Analysis
              </button>
            )}
          </div>
        ) : (
          <div className="table-wrap" style={{ border: 'none', borderRadius: 0 }}>
            <table className="data-table">
              <thead>
                <tr>
                  <th>Patient</th>
                  <th>Patient ID</th>
                  <th>Date</th>
                  <th>Time</th>
                  <th>Analysis ID</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((r, i) => {
                  const d = r.timestamp ? new Date(r.timestamp) : null;
                  return (
                    <tr
                      key={r.id || r.timestamp || i}
                      style={{ cursor: 'pointer' }}
                      onClick={() => navigate('/history', { state: { result: r, patient_id: r.patient_id, timestamp: r.timestamp } })}
                    >
                      <td>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <div style={{ width: 32, height: 32, borderRadius: '50%', background: 'rgba(27,108,168,0.1)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, fontSize: '0.75rem', fontWeight: 700, color: 'var(--blue-primary)' }}>
                            {(r.full_name || 'N').charAt(0).toUpperCase()}
                          </div>
                          <span style={{ fontWeight: 500 }}>{r.full_name || 'Unknown'}</span>
                        </div>
                      </td>
                      <td><span className="badge badge-blue">{r.patient_id || 'N/A'}</span></td>
                      <td style={{ color: 'var(--text-secondary)' }}>{d ? d.toLocaleDateString() : 'N/A'}</td>
                      <td style={{ color: 'var(--text-secondary)', fontFamily: 'var(--font-mono)', fontSize: '0.82rem' }}>
                        {d ? d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : 'N/A'}
                      </td>
                      <td style={{ fontFamily: 'var(--font-mono)', fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                        {r.analysisId || r.AnalysisID || '—'}
                      </td>
                      <td style={{ textAlign: 'right' }}>
                        <ChevronRight size={16} style={{ color: 'var(--text-muted)' }} />
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Monitoring modal */}
      <MonitoringModal
        open={monitorOpen}
        onClose={() => setMonitorOpen(false)}
        report={monitorReport}
      />
    </AppShell>
  );
}
